import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useParticipant } from '../../contexts/ParticipantContext';
import InteractiveUIViewer from '../../components/participant/InteractiveUIViewer';
import { getScenario, getDefaultScenario } from '../../data/taskScenarios';
import { BrCard, BrButton } from '@govbr-ds/react-components';

export default function ParticipantTaskPage() {
    const navigate = useNavigate();
    const { taskIndex } = useParams<{ taskIndex: string }>();
    const { state, getCurrentTask, dispatch } = useParticipant();

    const currentTaskIndex = parseInt(taskIndex || '0', 10);

    // Redirect if no session
    useEffect(() => {
        if (!state.session) {
            navigate('/participante/onboarding');
        }
    }, [state.session, navigate]);

    // Initialize interaction tracking for this task
    useEffect(() => {
        dispatch({
            type: 'UPDATE_INTERACTION',
            payload: {
                time_on_page_ms: 0,
                scroll_events: [],
                click_events: [],
                max_scroll_depth: 0
            }
        });
    }, [currentTaskIndex, dispatch]);

    if (!state.session) {
        return null;
    }

    const currentTask = getCurrentTask();

    // Get scenario based on task type and phase, or use default
    const scenario = currentTask
        ? getScenario(currentTask.task_type, currentTask.process_phase) || getDefaultScenario(currentTaskIndex)
        : getDefaultScenario(currentTaskIndex);

    const handleContinue = () => {
        navigate(`/participante/feedback/${currentTaskIndex}`);
    };

    // Mock ui_spec for demo if not available
    const uiSpec = currentTask?.ui_spec || {
        component: 'container',
        children: [
            {
                component: 'br-header',
                props: { title: 'Consulta Processual', subtitle: 'Tribunal Superior do Trabalho' }
            },
            {
                component: 'br-card',
                props: { title: 'Status do Processo' },
                children: [
                    {
                        component: 'br-text',
                        props: { content: 'Seu processo está sendo analisado pelo tribunal.' }
                    }
                ]
            },
            {
                component: 'br-message',
                props: {
                    type: 'info',
                    content: 'Não há prazos pendentes para este processo.'
                }
            }
        ]
    };

    return (
        <div className="container-lg py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-lg-10 d-flex flex-column gap-5">
                    {/* Scenario card */}
                    <BrCard>
                        <div className="p-4">
                            <div className="d-flex align-items-start gap-4">
                                <span className="text-3xl" aria-hidden="true">📋</span>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-80 mb-2 mt-0">
                                        {scenario.title}
                                    </h2>
                                    <p className="text-gray-60 mb-3">
                                        {scenario.description}
                                    </p>
                                    <p className="text-sm text-primary-default font-medium m-0">
                                        <strong>Instrução:</strong> {scenario.instruction}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </BrCard>

                    {/* Interactive UI */}
                    <section aria-label="Interface Interativa">
                        <InteractiveUIViewer
                            uiSpec={uiSpec}
                            onInteraction={(type, data) => {
                                console.log('Interaction:', type, data);
                            }}
                        />
                    </section>

                    {/* Continue button */}
                    <BrCard>
                        <div className="p-4 text-center">
                            <p className="text-gray-60 mb-4">
                                Quando terminar de explorar a interface, clique no botão abaixo.
                            </p>
                            <BrButton
                                primary
                                onClick={handleContinue}
                                size="large"
                                className="w-full sm:w-auto"
                            >
                                Concluí - Ir para Avaliação
                            </BrButton>
                        </div>
                    </BrCard>
                </div>
            </div>
        </div>
    );
}
